// Single source of truth for the *effective* per-status configuration in
// the Lanes module — colour, label (EN + optional PL), applicability
// flags, and KPI category. Built by layering admin overrides (stored in
// the BE, /admin/lane-statuses) over the FE's compile-time defaults.
//
// Phase 1: the 14 slugs are still code-owned (laneStatus.ts) — this
// module only merges editable *attributes* on top. Add / remove of slugs
// is a later phase.
//
// Apply model mirrors laneStatusColours.ts: a module-level `current`
// registry is hydrated synchronously at boot (cache → BE) and re-applied
// after every settings edit. Because it's a plain module singleton, the
// pure helpers in laneGrouping.ts and mockLaneData.ts can read a
// status's category/flags without threading config through their
// signatures, and the colour CSS variables are written to
// documentElement the same way as before.

import {
    STATUS_SLUGS,
    STATUS_LABEL_KEYS,
    STATUS_DEFAULT_CATEGORY,
    STATUS_DEFAULT_FLAGS,
    type StatusSlug,
    type StatusCategory,
    type StatusFlags,
} from "./laneStatus";
import { DEFAULT_STATUS_COLOURS, deriveColours } from "./laneStatusColours";
import { apiFetch, getSessionToken } from "../../api/apiFetch";
import type { Language } from "../../i18n";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

export interface StatusConfig {
    bar_colour: string;
    label_en: string;
    label_pl: string | null;
    flags: StatusFlags;
    category: StatusCategory;
}

// The BE row shape from /admin/lane-statuses. Every attribute column is
// nullable — null means "no override, use the FE default".
export interface StatusConfigRow {
    slug: string;
    bar_colour: string | null;
    label_en: string | null;
    label_pl: string | null;
    has_totes: boolean | null;
    has_auctions: boolean | null;
    has_auction_end_date: boolean | null;
    category: string | null;
}

const VALID_CATEGORIES: ReadonlySet<string> = new Set<StatusCategory>([
    "auction", "outbound", "stock", "idle", "unprocessed", "none",
]);

const isStatusSlug = (s: string): s is StatusSlug =>
    (STATUS_SLUGS as readonly string[]).includes(s);

// ---- defaults ----

const defaultConfig = (slug: StatusSlug): StatusConfig => ({
    bar_colour: DEFAULT_STATUS_COLOURS[slug],
    label_en: en[STATUS_LABEL_KEYS[slug]] ?? slug,
    label_pl: pl[STATUS_LABEL_KEYS[slug]] ?? null,
    flags: { ...STATUS_DEFAULT_FLAGS[slug] },
    category: STATUS_DEFAULT_CATEGORY[slug],
});

const buildDefaults = (): Record<StatusSlug, StatusConfig> => {
    const out = {} as Record<StatusSlug, StatusConfig>;
    for (const slug of STATUS_SLUGS) out[slug] = defaultConfig(slug);
    return out;
};

// Fresh copy of the compile-time defaults for one slug — used by the
// settings screen to restore a row locally after a reset.
export const defaultStatusConfig = (slug: StatusSlug): StatusConfig => defaultConfig(slug);

// Layer BE override rows over the compile-time defaults. A NULL/absent
// column leaves the default in place; only an explicit value overrides.
export const mergeOverrides = (rows: StatusConfigRow[]): Record<StatusSlug, StatusConfig> => {
    const out = buildDefaults();
    for (const row of rows) {
        if (!isStatusSlug(row.slug)) continue;
        const c = out[row.slug];
        if (typeof row.bar_colour === "string" && /^#[0-9a-fA-F]{6}$/.test(row.bar_colour)) {
            c.bar_colour = row.bar_colour.toUpperCase();
        }
        if (typeof row.label_en === "string" && row.label_en.trim() !== "") {
            c.label_en = row.label_en;
        }
        // label_pl override may be set to an explicit string; an empty /
        // null value falls back to label_en at read time.
        if (typeof row.label_pl === "string" && row.label_pl.trim() !== "") {
            c.label_pl = row.label_pl;
        }
        if (typeof row.has_totes === "boolean") c.flags.totes = row.has_totes;
        if (typeof row.has_auctions === "boolean") c.flags.auctions = row.has_auctions;
        if (typeof row.has_auction_end_date === "boolean") c.flags.auctionEndDate = row.has_auction_end_date;
        if (typeof row.category === "string" && VALID_CATEGORIES.has(row.category)) {
            c.category = row.category as StatusCategory;
        }
    }
    return out;
};

// ---- module-level registry ----

let current: Record<StatusSlug, StatusConfig> = buildDefaults();

// Write every status's four colour CSS variables to documentElement so
// the whole module rethemes in place — identical contract to
// laneStatusColours.applyStatusColours, but sourced from the merged
// config rather than a raw overrides map.
const applyColours = (cfg: Record<StatusSlug, StatusConfig>): void => {
    const root = document.documentElement;
    for (const slug of STATUS_SLUGS) {
        const { bg, fg, border, bar } = deriveColours(cfg[slug].bar_colour);
        root.style.setProperty(`--status-${slug}-bg`, bg);
        root.style.setProperty(`--status-${slug}-fg`, fg);
        root.style.setProperty(`--status-${slug}-border`, border);
        root.style.setProperty(`--status-${slug}-bar`, bar);
    }
};

// Swap in a fully-resolved effective config (used by the settings screen
// for live, optimistic updates) and re-apply the colour variables.
export const applyEffectiveConfig = (cfg: Record<StatusSlug, StatusConfig>): void => {
    current = cfg;
    applyColours(current);
};

const setFromRows = (rows: StatusConfigRow[]): void => {
    applyEffectiveConfig(mergeOverrides(rows));
};

// ---- accessors (synchronous; safe for pure helpers) ----

export const allStatusConfigs = (): Array<{ slug: StatusSlug } & StatusConfig> =>
    STATUS_SLUGS.map(slug => ({ slug, ...current[slug] }));

export const getStatusConfig = (slug: StatusSlug): StatusConfig => current[slug];
export const statusCategory = (slug: StatusSlug): StatusCategory => current[slug].category;
export const statusFlags = (slug: StatusSlug): StatusFlags => current[slug].flags;

// Resolve the display label for a status in the active language. Polish
// is optional per status — fall back to the English label when blank.
export const statusLabel = (slug: StatusSlug, language: Language): string => {
    const c = current[slug];
    if (language === "pl" && c.label_pl && c.label_pl.trim() !== "") return c.label_pl;
    return c.label_en;
};

// ---- localStorage cache (synchronous boot-time apply, no flash) ----

const CACHE_KEY = "lanes-status-config-cache";

const readCache = (): StatusConfigRow[] | null => {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as StatusConfigRow[]) : null;
    } catch {
        return null;
    }
};

const writeCache = (rows: StatusConfigRow[]): void => {
    try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(rows)); } catch { /* storage disabled */ }
};

export const clearConfigCache = (): void => {
    try { window.localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
};

// ---- BE I/O ----

export const fetchStatusConfig = async (): Promise<StatusConfigRow[]> => {
    const res = await apiFetch(`/admin/lane-statuses`, {});
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const rows = await res.json();
    return Array.isArray(rows) ? rows : [];
};

// Partial attribute patch for one status. Send only the fields that
// changed; the BE leaves the rest untouched.
export interface StatusConfigPatch {
    bar_colour?: string | null;
    label_en?: string | null;
    label_pl?: string | null;
    has_totes?: boolean | null;
    has_auctions?: boolean | null;
    has_auction_end_date?: boolean | null;
    category?: StatusCategory | null;
}

export const patchStatusConfig = async (slug: StatusSlug, patch: StatusConfigPatch): Promise<void> => {
    const res = await apiFetch(`/admin/lane-statuses/${slug}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const resetStatusConfig = async (slug: StatusSlug): Promise<void> => {
    const res = await apiFetch(`/admin/lane-statuses/${slug}`, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export const resetAllStatusConfig = async (): Promise<void> => {
    const res = await apiFetch(`/admin/lane-statuses`, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

// Refresh the cache + registry from the BE and return the merged config.
// Settings uses this on mount so it always edits the server-of-record.
export const refreshStatusConfig = async (): Promise<Record<StatusSlug, StatusConfig>> => {
    const rows = await fetchStatusConfig();
    writeCache(rows);
    setFromRows(rows);
    return current;
};

// ---- boot ----

// Apply the cached config synchronously (no flash of defaults), then
// fetch the BE source-of-truth in the background. On failure the cached
// (or default) registry stays applied — Lanes never blanks.
export const bootstrapStatusConfig = (): void => {
    const cached = readCache();
    if (cached) setFromRows(cached);
    else applyColours(current); // ensure colour vars are written even with pure defaults
    // /admin/lane-statuses is admin-protected, so fetching it before the
    // user has logged in just 401s and spams the console. Skip the
    // pre-warm when there's no session token — the admin Lanes page
    // (and LaneStatusColoursAdmin) fetch on mount once authenticated.
    if (!getSessionToken()) return;
    void fetchStatusConfig()
        .then(rows => { writeCache(rows); setFromRows(rows); })
        .catch(() => { /* keep cached / default registry */ });
};
